'use strict';

const constants = require('./constants');
const addressValidation = require('./addressValidation');
const controlledPhoneInput = require('../components/controlledPhoneInput');
const formFields = require('../components/formFields');

/**
 * Sanitizes a state name by removing periods, normalizing Unicode characters,
 * and stripping diacritical marks (accents).
 *
 * @param {string} state - The state name to sanitize.
 * @returns {string} The sanitized state name.
 */
function sanitizeStateName(state) {
    return state.replace(/\./g, "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}


/**
 * Handles the selection of a state in a dropdown selector.
 * Attempts to match the provided state with an option in the selector
 * by its ID or by checking aliases defined in the `data-aliases` attribute.
 * If a match is found, the corresponding option is selected, and the selector
 * is updated and disabled. If no match is found, an error is logged.
 *
 * @param {string} state - The name of the state to select.
 * @param {jQuery} $selector - The jQuery object representing the dropdown selector.
 * @returns {boolean} True when an official option matched and was selected.
 */
function handleStateSelection(state, $selector) {
    const stateText = sanitizeStateName(state).toUpperCase();
    let $option = null;

    $option = $selector.find(`option#${stateText}`);

    if ($option.length === 0) {
        $option = $selector.find('option').filter(function() {
            const aliasesStr = $(this).attr('data-aliases');
            if (aliasesStr && aliasesStr !== 'null') {
                try {
                    const aliases = JSON.parse(aliasesStr);
                    return aliases.some(alias => sanitizeStateName(alias).toUpperCase() === stateText);
                } catch (e) {
                    console.error('Failed to parse aliases:', e);
                }
            }
            return false;
        });
    }

    if ($option.length > 0) {
        $option.prop('selected', true);
        $selector.val($option.attr('id'));
        $selector.parent().addClass('filled');
        $selector.prop('disabled', true);
    } else {
        console.error(`No match found for state`);
    }

    return $option.length > 0;
}

/**
 * Handles the coverage of a delivery address based on the provided place.
 *
 * @param {Object} place - The place object containing address details.
 * @param {string|null} context - Context of the coverage check ('checkout', 'addressBook', 'editAddress', or null).
 */
function checkCoverage(place, $form, context) {
    const selectedDeliveryAddress = getAddressFormatted(place);
    if (!selectedDeliveryAddress) {
        return {
            hasCoverage: false,
            errorMsg: ''
        };
    }

    return $.ajax({
        url: $form.data('check-coverage-url'),
        type: 'post',
        dataType: 'json',
        data: {
            latitude: selectedDeliveryAddress.lat,
            longitude: selectedDeliveryAddress.lng,
            addressName: selectedDeliveryAddress.name,
            addressDetails: selectedDeliveryAddress.addressDetails,
            zipCode: selectedDeliveryAddress.zipCode || $form.find('[name$="_postalCode"]').val() || '',
            context: context
        }
    }).then(function (response) {
        return {
            hasCoverage: response.hasCoverage,
            errorMsg: response.errorMsg || '',
            isTechnicalError: response.isTechnicalError || false,
            storeHasChanged: response.storeHasChanged || false,
            storeId: response.storeId || null
        };
    }).catch(function (err) {
        console.error("Error checking coverage");
        return {
            hasCoverage: false,
            errorMsg: '',
            isTechnicalError: true,
            storeHasChanged: false,
            storeId: null
        };
    });
}

/**
 * Shows error messages in the address form
 * @param {jQuery} $modalAddressForm - The address form element
 * @param {Object} serviceResponse - The response from the service call
 * @returns {void}
 */
function showAddressErrorMessage($modalAddressForm, serviceResponse) {
    var $postalcode = $modalAddressForm.find('input.postalcodeTxt');
    var $submitButton = $modalAddressForm.find('button[type="submit"]');
    var $scrollableContainer = $modalAddressForm.closest('.tab-add-address');

    var isTechnicalError = !serviceResponse || serviceResponse.isTechnicalError;
    var markPostalError = !serviceResponse || serviceResponse.markPostalError !== false;

    var $messageContainer = $modalAddressForm.find('.address-form-error_container');

    $submitButton = $submitButton.length ? $submitButton : $('div.next-step-button > button.submit-shipping');

    if (serviceResponse && serviceResponse.errorMsg) {
        $messageContainer.find('.address-form-error_content').html(serviceResponse.errorMsg);
    }

    if (markPostalError) {
        $postalcode.addClass('address-form-error');
    }
    $messageContainer.toggleClass('address-form-error--no-postal', !markPostalError);
    $messageContainer.removeClass('d-none');
    $submitButton.attr('disabled', true);

    const mobileContainer = $('.ua-mobile');
    if (mobileContainer.length > 0) {
        var $containerToScroll = $scrollableContainer.length ? $scrollableContainer : $('.ua-mobile');
        $containerToScroll.animate({
            scrollTop: 0
        }, 300);
    }
}


/**
 * Clears errors related to address without coverage.
 * @requires jQuery
 * @returns {void}
 */
function clearNotCoverageMessages ($modalAddressForm) {
    var $postalcode = $modalAddressForm.find('input.postalcodeTxt');
    var $messageErrorContainer = $modalAddressForm.find('.address-form-error_container');
    var $submitButton = $modalAddressForm.find('button[type="submit"]');

    $submitButton = $submitButton.length ? $submitButton : $('div.next-step-button > button.submit-shipping');

    $postalcode.removeClass('address-form-error');
    $messageErrorContainer.removeClass('address-form-error--no-postal');
    $messageErrorContainer.addClass('d-none');
    $submitButton.attr('disabled', false);
}

function getAddressFormatted(addressFormDetails) {
    let selectedDeliveryAddress = null;

    if (addressFormDetails.geometry) {
        selectedDeliveryAddress = {
            name: '',
            addressDetails: '',
            lat: addressFormDetails.geometry.location.lat(),
            lng: addressFormDetails.geometry.location.lng()
        };

        if (addressFormDetails.address_components) {
            const addressModel = require('../components/deliveryModal/addressModel');
            const { addressName, addressDetails, zipCode } = addressModel.formatAddressComponents(addressFormDetails);

            selectedDeliveryAddress.name = addressName;
            selectedDeliveryAddress.addressDetails = addressDetails;
            selectedDeliveryAddress.zipCode = zipCode;
        }
    }

    return selectedDeliveryAddress;
}

/**
 * Initializes postal code validation to limit input to 5 characters
 * @param {jQuery} $form - The form element containing postal code inputs
 * @param {string} inputClass - Optional CSS class selector for the postal code input (default: '.postalcodeTxt')
 */
function initPostalCodeValidation($form, inputClass) {
    var selector = inputClass || '.postalcodeTxt';
    $form.on('input', selector, function() {
        var value = $(this).val();
        if (value.length > 5) {
            $(this).val(value.slice(0, 5));
        }
    });
}

/**
 * Writes a phone to the visible tel input and to its hidden payload input. A
 * national 10-digit number is shown formatted; any other value is written as
 * received, so it keeps its validation error instead of being shortened.
 * Before initControlledPhoneInput runs there is no hidden input yet; it is
 * created later from the visible value.
 *
 * @param {jQuery} $input - The visible tel input.
 * @param {string} value - The phone value.
 */
function setPhoneValue($input, value) {
    const phone = value ? String(value) : '';
    const digits = addressValidation.digitsOnly(phone);
    const isNationalNumber = constants.ADDRESS_FIELD_CONTRACT.phone.pattern.test(digits);
    const displayValue = isNationalNumber ? controlledPhoneInput.getFormattedValueFromString(digits) : phone;

    $input.val(displayValue).attr('data-display', displayValue);
    $input.siblings('input[type="hidden"]').val(isNationalNumber ? digits : phone);
}

/**
 * Writes a value set by code into an address field, then refreshes its
 * floating label and validation. No input or change event is dispatched, so
 * the listeners reserved for user edits (phone formatting, coverage message,
 * submit button) keep their state.
 *
 * @param {jQuery} $field - The field element.
 * @param {string} value - The value to write.
 */
function writeFieldValue($field, value) {
    const hadValue = String($field.val() || '').trim() !== '';

    if ($field.is('input[type="tel"]')) {
        setPhoneValue($field, value);
    } else {
        $field.val(value || '');
    }

    formFields.methods.handleInputState($field);
    addressValidation.validateWrittenField($field, hadValue);
}

module.exports = {
    handleStateSelection: handleStateSelection,
    sanitizeStateName: sanitizeStateName,
    checkCoverage: checkCoverage,
    getAddressFormatted: getAddressFormatted,
    showAddressErrorMessage: showAddressErrorMessage,
    clearNotCoverageMessages: clearNotCoverageMessages,
    initPostalCodeValidation: initPostalCodeValidation,
    writeFieldValue: writeFieldValue
}
